import { Fragment, useCallback, useEffect, useState } from 'react';
import box from '~/assets/icons/box.svg';
import { SectionHeading } from '~/components/ui/SectionHeading';
import {
	IExchangeItemHouse,
	IExchangeItemNumberPlate,
	IExchangeItemVehicle,
	rage,
	useInputAction,
	useObservable,
} from '~/rage';
import { InputAction } from '~/shared';
import {
	setPropertyList,
	setPropertyListIndex,
	setPropertyListIndexNext,
	setPropertyListIndexPrev,
	setPropertyListMaxIndex,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { Divider } from '../Divider';
import { ListItem } from '../ListItem';
import { MenuTabs } from '../MenuTabs';
import {
	StyledContainer,
	StyledHead,
	StyledPropertyContainer,
} from './PropertyList.styled';

const PropertyList: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const [scrollOffset, setScrollOffset] = useState<number>(0);
	const [selectionHidden, setSelectionHidden] = useState<boolean>(false);
	const tabIndex = useLayoutSelector(
		(state) => state.propertyExchange.tabIndex,
	);
	const propertyListIndex = useLayoutSelector(
		(state) => state.propertyExchange.propertyListIndex,
	);
	const propertyList = useLayoutSelector(
		(state) => state.propertyExchange.propertyList,
	);
	const ownedItems = useObservable(rage.exchange.getOwnedItems());

	useInputAction(InputAction.CommonMenuNavUp, () =>
		dispatch(setPropertyListIndexPrev()),
	);
	useInputAction(InputAction.CommonMenuNavDown, () =>
		dispatch(setPropertyListIndexNext()),
	);
	useInputAction(InputAction.CommonMenuAccept, async () => {
		await rage.exchange.placeItem(propertyList[propertyListIndex].id);
	});

	const handleScroll = useCallback((e: React.UIEvent<HTMLElement>) => {
		const scrollTop = (e as any).target?.scrollTop;
		if (scrollTop) {
			setScrollOffset(scrollTop);
		}
	}, []);

	useEffect(() => {
		if (tabIndex === 0) {
			const filteredItems = ownedItems.filter((item) =>
				rage.exchange.isItemVehicle(item),
			);
			dispatch(setPropertyListMaxIndex(filteredItems.length - 1));
			dispatch(setPropertyList(filteredItems));
			dispatch(setPropertyListIndex(0));
		}
		if (tabIndex === 1) {
			const filteredItems = ownedItems.filter((item) =>
				rage.exchange.isItemHouse(item),
			);
			dispatch(setPropertyListMaxIndex(filteredItems.length - 1));
			dispatch(setPropertyList(filteredItems));
			dispatch(setPropertyListIndex(0));
		}
		if (tabIndex === 2) {
			const filteredItems = ownedItems.filter((item) =>
				rage.exchange.isItemNumberPlate(item),
			);
			dispatch(setPropertyListMaxIndex(filteredItems.length - 1));
			dispatch(setPropertyList(filteredItems));
			dispatch(setPropertyListIndex(0));
		}
	}, [tabIndex, ownedItems]);

	return (
		<StyledContainer>
			<StyledHead>
				<SectionHeading icon={box} text={'Ваше имущество'} />
			</StyledHead>
			<Divider
				style={{ marginTop: 20, marginBottom: 20, width: 'calc(100% - 20px)' }}
			/>
			<MenuTabs />
			<StyledPropertyContainer onScroll={handleScroll}>
				{tabIndex === 0 &&
					(propertyList as IExchangeItemVehicle[]).map((item, index) => (
						<Fragment key={index}>
							<ListItem
								id={item.id}
								type={item.type}
								text={item.vehicleName}
								mode={'add'}
								number={item.numberPlate}
								scrollOffset={scrollOffset}
								active={index === propertyListIndex}
								onClick={() => dispatch(setPropertyListIndex(index))}
								onMouseEnter={() => setSelectionHidden(true)}
								onMouseLeave={() => setSelectionHidden(false)}
								selectionHidden={selectionHidden && index === propertyListIndex}
							/>
							<Divider />
						</Fragment>
					))}
				{tabIndex === 1 &&
					(propertyList as IExchangeItemHouse[]).map((item, index) => (
						<Fragment key={index}>
							<ListItem
								id={item.id}
								type={item.type}
								text={`Дом №${item.houseId}`}
								mode={'add'}
								scrollOffset={scrollOffset}
								active={index === propertyListIndex}
								onClick={() => dispatch(setPropertyListIndex(index))}
								onMouseEnter={() => setSelectionHidden(true)}
								onMouseLeave={() => setSelectionHidden(false)}
								selectionHidden={selectionHidden && index === propertyListIndex}
							/>
							<Divider />
						</Fragment>
					))}
				{tabIndex === 2 &&
					(propertyList as IExchangeItemNumberPlate[]).map((item, index) => (
						<Fragment key={index}>
							<ListItem
								id={item.id}
								type={item.type}
								text={''}
								mode={'add'}
								number={item.numberPlate}
								scrollOffset={scrollOffset}
								active={index === propertyListIndex}
								onClick={() => dispatch(setPropertyListIndex(index))}
								onMouseEnter={() => setSelectionHidden(true)}
								onMouseLeave={() => setSelectionHidden(false)}
								selectionHidden={selectionHidden && index === propertyListIndex}
							/>
							<Divider />
						</Fragment>
					))}
			</StyledPropertyContainer>
		</StyledContainer>
	);
};

export { PropertyList };
